// src/utils/navigationHelper.js

import SecureStorageService from '../services/secureStorageService';

/**
 * Get the appropriate dashboard URL based on user role
 * 
 * @returns {string} Dashboard URL for the current user's role
 * 
 * Role-based routing:
 * - Admin: /adminDashboard
 * - PartnerAdmin: /partnerDashboard
 * - User (or no role): /guides
 */
export const getDashboardUrl = () => {
  const roles = SecureStorageService.getRoles() || [];
  
  // Check roles in priority order
  if (roles.includes('Admin')) {
    return '/adminDashboard';
  }
  
  if (roles.includes('PartnerAdmin')) {
    return '/partnerDashboard';
  }
  
  // Default to guides for regular users or unknown roles
  return '/guides';
};

/**
 * Get dashboard label based on user role
 * 
 * @param {function} t - i18next translation function
 * @returns {string} Translated dashboard label
 */
export const getDashboardLabel = (t) => {
  const roles = SecureStorageService.getRoles() || [];
  
  if (roles.includes('Admin')) {
    return t('header.nav.dashboard', { ns: 'common' }) || 'Admin Dashboard';
  }
  
  if (roles.includes('PartnerAdmin')) {
    return t('header.nav.dashboard', { ns: 'common' }) || 'Partner Dashboard';
  }
  
  return t('header.nav.guides', { ns: 'common' }) || 'Guides';
};

/**
 * Check if user has any of the specified roles
 * 
 * @param {string[]} requiredRoles - Array of role names to check
 * @returns {boolean} True if user has at least one of the roles
 */
export const hasAnyRole = (requiredRoles) => {
  const userRoles = SecureStorageService.getRoles() || [];
  return requiredRoles.some(role => userRoles.includes(role));
};

/**
 * Check if user has all specified roles
 * 
 * @param {string[]} requiredRoles - Array of role names to check
 * @returns {boolean} True if user has all the roles
 */
export const hasAllRoles = (requiredRoles) => {
  const userRoles = SecureStorageService.getRoles() || [];
  return requiredRoles.every(role => userRoles.includes(role));
};

/**
 * Get user's primary role (highest priority)
 * 
 * @returns {string} Primary role name
 */
export const getPrimaryRole = () => {
  const roles = SecureStorageService.getRoles() || [];
  
  if (roles.includes('Admin')) return 'Admin';
  if (roles.includes('PartnerAdmin')) return 'PartnerAdmin';
  return 'User';
};

const navigationHelper = {
  getDashboardUrl,
  getDashboardLabel,
  hasAnyRole,
  hasAllRoles,
  getPrimaryRole
};

export default navigationHelper;
